import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeBrackets } from '../utils/queryUtils';

// Page-level filters always sort ahead of secondary params like search/filter[...].
const PRIORITY_PARAM_ORDER = ['range', 'start', 'end'];

function reorderParams(params: URLSearchParams): URLSearchParams {
  const ordered = new URLSearchParams();

  PRIORITY_PARAM_ORDER.forEach((key) => {
    const value = params.get(key);
    if (value !== null) {
      ordered.set(key, value);
    }
  });

  params.forEach((value, key) => {
    if (!PRIORITY_PARAM_ORDER.includes(key)) {
      ordered.append(key, value);
    }
  });

  return ordered;
}

export const useActionLogsPageQueryParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  /**
   * Merges `params` into the current URL search string, preserving unrelated
   * keys so multiple writers (date range, search, filters) can coexist.
   * @param params Query key/value pairs; `undefined` deletes the key.
   */
  const setParams = useCallback(
    (params: Record<string, string | undefined>) => {
      const nextParams = new URLSearchParams(window.location.search);
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, value);
        }
      });

      const nextSearch = decodeBrackets(reorderParams(nextParams).toString());
      const currentSearch = window.location.search.replace(/^\?/, '');
      if (nextSearch !== currentSearch) {
        navigate({ search: nextSearch }, { replace: true });
      }
    },
    [navigate]
  );

  return { searchParams, setParams };
};
